import { useState, useCallback } from "react";
import { useLanguage, API } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useRecaptchaToken } from "../hooks/useRecaptchaToken";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  X,
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Briefcase,
  MessageSquare,
  Clock,
  Calendar,
  AlertTriangle,
  Loader2,
  CheckCircle
} from "lucide-react";

const regions = {
  france: [
    "Auvergne–Rhône-Alpes",
    "Bourgogne–Franche-Comté",
    "Bretagne",
    "Centre–Val de Loire",
    "Corse",
    "Grand Est",
    "Hauts-de-France",
    "Île-de-France",
    "Normandie",
    "Nouvelle-Aquitaine",
    "Occitanie",
    "Pays de la Loire",
    "Provence–Alpes–Côte d'Azur"
  ],
  suisse: [
    "Vaud",
    "Genève",
    "Fribourg",
    "Neuchâtel",
    "Valais",
    "Jura"
  ],
  espana: [
    "Andalucía",
    "Aragón",
    "Principado de Asturias",
    "Islas Baleares",
    "Canarias",
    "Cantabria",
    "Castilla-La Mancha",
    "Castilla y León",
    "Cataluña",
    "Comunidad Valenciana",
    "Extremadura",
    "Galicia",
    "Comunidad de Madrid",
    "Región de Murcia",
    "Comunidad Foral de Navarra",
    "País Vasco",
    "La Rioja"
  ],
  espana_ciudades: [
    "Ceuta",
    "Melilla"
  ]
};

const positions = {
  fr: [
    "Comptable",
    "Expert-comptable",
    "Directeur administratif et financier / Directeur financier",
    "Président-directeur général / Président exécutif",
    "Directeur exécutif / Directeur général délégué",
    "Directeur financier",
    "Directeur général",
    "Directeur informatique et technologie"
  ],
  es: [
    "Contable",
    "Experto contable",
    "Director administrativo y financiero / Director financiero",
    "Presidente / Presidente ejecutivo",
    "Director ejecutivo / Director general adjunto",
    "Director financiero",
    "Director general",
    "Director de informática y tecnología"
  ],
  en: [
    "Accountant",
    "Certified Public Accountant",
    "Administrative & Finance Director / Chief Financial Officer",
    "CEO / Executive President",
    "Executive Director / Deputy General Director",
    "Chief Financial Officer",
    "General Manager",
    "IT & Technology Director"
  ]
};

const translations = {
  fr: {
    title: "Demande de Démo Personnalisée",
    contactInfo: "Informations de contact",
    lastName: "Nom",
    firstName: "Prénom",
    email: "E-mail",
    phone: "Téléphone",
    company: "Entreprise",
    region: "De quelle région dépendez-vous ?",
    regionPlaceholder: "Sélectionnez une région",
    frenchRegions: "Régions France",
    swissCantons: "Cantons Suisse",
    position: "Poste",
    positionPlaceholder: "Sélectionnez un poste",
    reason: "Motif de votre demande",
    reasonPlaceholder: "Décrivez votre besoin en quelques mots...",
    preferredTime: "À quelle heure préférez-vous ?",
    preferredDate: "À quelle date préférez-vous ?",
    importantInfo: "Information importante",
    consent: "J'accepte que Documatch gère ma demande et je consens au traitement de mes données personnelles conformément à la politique de confidentialité.",
    processedImmediately: "La demande est prise en considération immédiatement",
    notice24h: "La cita requiere 24h minimum",
    emailConfirmation: "Confirmation par email avec lien de connexion",
    yesAccept: "Oui, j'accepte",
    noDecline: "Non, je n'accepte pas",
    submit: "Envoyer la demande",
    submitting: "Envoi en cours...",
    success: "Votre demande de démo a été envoyée avec succès ! Nous vous contacterons sous 24h.",
    error: "Une erreur s'est produite. Veuillez réessayer.",
    required: "Ce champ est obligatoire",
    invalidEmail: "Email invalide",
    mustAccept: "Vous devez accepter les conditions pour continuer"
  },
  es: {
    title: "Solicitud de Demo Personalizada",
    contactInfo: "Información de contacto",
    lastName: "Apellidos",
    firstName: "Nombre",
    email: "Correo electrónico",
    phone: "Teléfono",
    company: "Empresa",
    region: "¿De qué provincia depende?",
    regionPlaceholder: "Seleccione una provincia",
    spanishProvinces: "Provincias de España",
    spanishCities: "Ciudades autónomas",
    position: "Cargo",
    positionPlaceholder: "Seleccione un cargo",
    reason: "Motivo de su solicitud",
    reasonPlaceholder: "Describa su necesidad en pocas palabras...",
    preferredTime: "¿A qué hora prefiere la reunión?",
    preferredDate: "¿En qué fecha prefiere la reunión?",
    importantInfo: "Información importante",
    consent: "Acepto que Documatch gestione mi solicitud y consiento el tratamiento de mis datos personales conforme a la política de privacidad.",
    processedImmediately: "La solicitud se tiene en cuenta inmediatamente",
    notice24h: "La cita requiere un mínimo de 24h",
    emailConfirmation: "Confirmación por email con enlace de conexión",
    yesAccept: "Sí, acepto",
    noDecline: "No, no acepto",
    submit: "Enviar solicitud",
    submitting: "Enviando...",
    success: "¡Su solicitud de demo ha sido enviada con éxito! Le contactaremos en 24h.",
    error: "Se produjo un error. Por favor, inténtelo de nuevo.",
    required: "Este campo es obligatorio",
    invalidEmail: "Email inválido",
    mustAccept: "Debe aceptar las condiciones para continuar"
  },
  en: {
    title: "Personalized Demo Request",
    contactInfo: "Contact Information",
    lastName: "Last Name",
    firstName: "First Name",
    email: "Email",
    phone: "Phone",
    company: "Company",
    region: "Which region do you belong to?",
    regionPlaceholder: "Select a region",
    frenchRegions: "French Regions",
    swissCantons: "Swiss Cantons",
    position: "Position",
    positionPlaceholder: "Select a position",
    reason: "Reason for your request",
    reasonPlaceholder: "Describe your needs in a few words...",
    preferredTime: "Preferred time",
    preferredDate: "Preferred date",
    importantInfo: "Important Information",
    consent: "I accept that Documatch manages my request and I consent to the processing of my personal data in accordance with the privacy policy.",
    processedImmediately: "Request is processed immediately",
    notice24h: "Minimum 24h notice required",
    emailConfirmation: "Email confirmation with connection link",
    yesAccept: "Yes, I accept",
    noDecline: "No, I do not accept",
    submit: "Submit Request",
    submitting: "Submitting...",
    success: "Your demo request has been sent successfully! We will contact you within 24h.",
    error: "An error occurred. Please try again.",
    required: "This field is required",
    invalidEmail: "Invalid email",
    mustAccept: "You must accept the conditions to continue"
  }
};

export const DemoRequestModal = ({ isOpen, onClose, solutionName = "" }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.fr;
  const positionList = positions[language] || positions.fr;
  const getRecaptchaToken = useRecaptchaToken();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    email: "",
    phone: "",
    company: "",
    region: "",
    position: "",
    reason: solutionName ? `Demo pour ${solutionName}` : "",
    preferredTime: "",
    preferredDate: "",
    acceptTerms: null
  });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.lastName.trim()) newErrors.lastName = t.required;
    if (!formData.firstName.trim()) newErrors.firstName = t.required;
    if (!formData.email.trim()) {
      newErrors.email = t.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.invalidEmail;
    }
    if (!formData.phone.trim()) newErrors.phone = t.required;
    if (!formData.company.trim()) newErrors.company = t.required;
    // Region is only required for FR and EN, not for ES
    if (language !== "es" && !formData.region) newErrors.region = t.required;
    if (!formData.position) newErrors.position = t.required;
    if (!formData.reason.trim()) newErrors.reason = t.required;
    if (!formData.preferredTime) newErrors.preferredTime = t.required;
    if (!formData.preferredDate) newErrors.preferredDate = t.required;
    if (formData.acceptTerms !== true) newErrors.acceptTerms = t.mustAccept;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(language === "fr" ? "Veuillez remplir tous les champs obligatoires" : 
                  language === "es" ? "Por favor complete todos los campos obligatorios" :
                  "Please fill in all required fields");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const recaptchaToken = await getRecaptchaToken("demo_request");
      await axios.post(`${API}/request-demo`, {
        ...formData,
        language,
        solution: solutionName,
        recaptchaToken
      });
      
      setSubmitted(true);
      toast.success(t.success);
      
      // Reset form after 3 seconds and close
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          lastName: "",
          firstName: "",
          email: "",
          phone: "",
          company: "",
          region: "",
          position: "",
          reason: "",
          preferredTime: "",
          preferredDate: ""
        });
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error("Demo request error:", error);
      toast.error(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-white max-w-md">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              {language === "fr" ? "Demande envoyée !" : 
               language === "es" ? "¡Solicitud enviada!" : 
               "Request sent!"}
            </h3>
            <p className="text-slate-600">{t.success}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="demo-request-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="text-blue-600" size={24} />
            {t.title}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Contact Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <User size={20} className="text-blue-600" />
              {t.contactInfo}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lastName" className="text-slate-700">{t.lastName} *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  className={errors.lastName ? "border-red-500" : ""}
                  data-testid="demo-lastname"
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
              
              <div>
                <Label htmlFor="firstName" className="text-slate-700">{t.firstName} *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  className={errors.firstName ? "border-red-500" : ""}
                  data-testid="demo-firstname"
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              
              <div>
                <Label htmlFor="email" className="text-slate-700 flex items-center gap-1">
                  <Mail size={14} /> {t.email} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className={errors.email ? "border-red-500" : ""}
                  data-testid="demo-email"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <Label htmlFor="phone" className="text-slate-700 flex items-center gap-1">
                  <Phone size={14} /> {t.phone} *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className={errors.phone ? "border-red-500" : ""}
                  data-testid="demo-phone"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="company" className="text-slate-700 flex items-center gap-1">
                  <Building2 size={14} /> {t.company} *
                </Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleChange("company", e.target.value)}
                  className={errors.company ? "border-red-500" : ""}
                  data-testid="demo-company"
                />
                {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company}</p>}
              </div>
            </div>
          </div>

          {/* Region Section */}
          <div>
            <Label className="text-slate-700 flex items-center gap-1 mb-2">
              <MapPin size={14} className="text-blue-600" /> {t.region} {language !== "es" && "*"}
            </Label>
            <Select value={formData.region} onValueChange={(value) => handleChange("region", value)}>
              <SelectTrigger className={errors.region ? "border-red-500" : ""} data-testid="demo-region">
                <SelectValue placeholder={t.regionPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {language === "es" ? (
                  <>
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">
                      {t.spanishProvinces}
                    </div>
                    {regions.espana.map((provincia) => (
                      <SelectItem key={provincia} value={provincia}>{provincia}</SelectItem>
                    ))}
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 mt-2">
                      🏙️ {t.spanishCities}
                    </div>
                    {regions.espana_ciudades.map((ciudad) => (
                      <SelectItem key={ciudad} value={ciudad}>{ciudad}</SelectItem>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">
                      {t.frenchRegions}
                    </div>
                    {regions.france.map((region) => (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    ))}
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 mt-2">
                      {t.swissCantons}
                    </div>
                    {regions.suisse.map((canton) => (
                      <SelectItem key={canton} value={canton}>{canton}</SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region}</p>}
          </div>

          {/* Position Section */}
          <div>
            <Label className="text-slate-700 flex items-center gap-1 mb-2">
              <Briefcase size={14} className="text-blue-600" /> {t.position} *
            </Label>
            <Select value={formData.position} onValueChange={(value) => handleChange("position", value)}>
              <SelectTrigger className={errors.position ? "border-red-500" : ""} data-testid="demo-position">
                <SelectValue placeholder={t.positionPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {positionList.map((pos) => (
                  <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.position && <p className="text-red-500 text-xs mt-1">{errors.position}</p>}
          </div>

          {/* Reason Section */}
          <div>
            <Label htmlFor="reason" className="text-slate-700 flex items-center gap-1 mb-2">
              <MessageSquare size={14} className="text-blue-600" /> {t.reason} *
            </Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => handleChange("reason", e.target.value)}
              placeholder={t.reasonPlaceholder}
              className={`min-h-[100px] ${errors.reason ? "border-red-500" : ""}`}
              data-testid="demo-reason"
            />
            {errors.reason && <p className="text-red-500 text-xs mt-1">{errors.reason}</p>}
          </div>

          {/* Date and Time Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="preferredTime" className="text-slate-700 flex items-center gap-1 mb-2">
                <Clock size={14} className="text-blue-600" /> {t.preferredTime} *
              </Label>
              <Input
                id="preferredTime"
                type="time"
                value={formData.preferredTime}
                onChange={(e) => handleChange("preferredTime", e.target.value)}
                className={errors.preferredTime ? "border-red-500" : ""}
                data-testid="demo-time"
              />
              {errors.preferredTime && <p className="text-red-500 text-xs mt-1">{errors.preferredTime}</p>}
            </div>
            
            <div>
              <Label htmlFor="preferredDate" className="text-slate-700 flex items-center gap-1 mb-2">
                <Calendar size={14} className="text-blue-600" /> {t.preferredDate} *
              </Label>
              <Input
                id="preferredDate"
                type="date"
                min={getMinDate()}
                value={formData.preferredDate}
                onChange={(e) => handleChange("preferredDate", e.target.value)}
                className={errors.preferredDate ? "border-red-500" : ""}
                data-testid="demo-date"
              />
              {errors.preferredDate && <p className="text-red-500 text-xs mt-1">{errors.preferredDate}</p>}
            </div>
          </div>

          {/* Important Information Section */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-4">
            <h3 className="text-lg font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle size={20} /> {t.importantInfo}
            </h3>
            
            <p className="text-sm text-amber-900">{t.consent}</p>
            
            <div className="space-y-2 text-sm text-amber-800">
              <p className="flex items-center gap-2">
                <span className="text-green-600">✓</span> {t.processedImmediately}
              </p>
              <p className="flex items-center gap-2">
                <span className="text-amber-600">⏳</span> {t.notice24h}
              </p>
              <p className="flex items-center gap-2">
                <span className="text-blue-600">📧</span> {t.emailConfirmation}
              </p>
            </div>
            
            <div className="space-y-2 pt-2">
              <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-amber-100 transition-colors">
                <input
                  type="radio"
                  name="acceptTerms"
                  checked={formData.acceptTerms === true}
                  onChange={() => handleChange("acceptTerms", true)}
                  className="w-4 h-4 text-blue-600"
                  data-testid="demo-accept-yes"
                />
                <span className="text-amber-900">{t.yesAccept}</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-amber-100 transition-colors">
                <input
                  type="radio"
                  name="acceptTerms"
                  checked={formData.acceptTerms === false}
                  onChange={() => handleChange("acceptTerms", false)}
                  className="w-4 h-4 text-red-600"
                  data-testid="demo-accept-no"
                />
                <span className="text-amber-900">{t.noDecline}</span>
              </label>
              {errors.acceptTerms && <p className="text-red-500 text-xs">{errors.acceptTerms}</p>}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
            data-testid="demo-submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={20} className="mr-2 animate-spin" />
                {t.submitting}
              </>
            ) : (
              <>
                <Calendar size={20} className="mr-2" />
                {t.submit}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DemoRequestModal;
